import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, tap, catchError, throwError, map } from 'rxjs';
import { Permission } from '../models/permission.model';
import { ROLE_PERMISSIONS, RoleName } from '../models/role.model';
import { AuthUser } from '../models/user.model';
import { MFAService } from './mfa.service';
import { MFAChallenge, MFAMethod } from '../models/mfa.model';
import { environment } from '../../../../environments/environments';
import { API_ENDPOINTS } from '../../config/api-endpoints';
import { ToastService } from '../../services/toast.service';

interface LoginResponse {
  access_token?: string;
  session_token?: string;
  refresh_token?: string;
  expires_in?: number;
  user?: AuthUser;
  mfaChallenge?: MFAChallenge;
}

function normalizeLoginResponse(response: any): LoginResponse {
  const data = response?.data ?? {};
  return {
    ...response,
    access_token: response?.access_token ?? data?.access_token,
    session_token: response?.session_token ?? response?.sessionToken ?? response?.session ?? data?.session_token ?? data?.sessionToken ?? data?.session,
    refresh_token: response?.refresh_token ?? data?.refresh_token,
    expires_in: response?.expires_in ?? data?.expires_in,
    user: response?.user ?? data?.user,
    mfaChallenge: response?.mfaChallenge ?? data?.mfaChallenge,
  };
}

function currentLang(): 'en' | 'es' | 'fr' {
  const stored = (localStorage.getItem('bluedop_language') || '').toLowerCase();
  if (stored.startsWith('es')) return 'es';
  if (stored.startsWith('fr')) return 'fr';
  return 'en';
}

function authText(key: 'loginSuccess' | 'requestFailed' | 'invalidCredentials' | 'accountLocked' | 'inactiveDenied'): string {
  const lang = currentLang();
  const dict: Record<'en' | 'es' | 'fr', Record<typeof key, string>> = {
    en: {
      loginSuccess: 'Logged in successfully',
      requestFailed: 'Request failed.',
      invalidCredentials: 'Invalid credentials.',
      accountLocked: 'Account locked.',
      inactiveDenied: 'Access denied. User is inactive.',
    },
    es: {
      loginSuccess: 'Inicio de sesion correcto',
      requestFailed: 'La solicitud fallo.',
      invalidCredentials: 'Credenciales invalidas.',
      accountLocked: 'Cuenta bloqueada.',
      inactiveDenied: 'Acceso denegado. El usuario esta inactivo.',
    },
    fr: {
      loginSuccess: 'Connexion reussie',
      requestFailed: 'La requete a echoue.',
      invalidCredentials: 'Identifiants invalides.',
      accountLocked: 'Compte verrouille.',
      inactiveDenied: "Acces refuse. L'utilisateur est inactif.",
    },
  };

  return dict[lang][key];
}
@Injectable({ providedIn: 'root' })
export class AuthService {

  // Session & Signals
  private _currentUser = signal<AuthUser | null>(null);
  readonly currentUser = this._currentUser.asReadonly();

  private _accessToken = signal<string | null>(null);
  readonly accessToken = this._accessToken.asReadonly();

  // Observable for login state
  private _isLoggedIn$ = new BehaviorSubject<boolean>(this.hasToken());
  public isLoggedIn$ = this._isLoggedIn$.asObservable();

  // Session expiry modal flag
  readonly sessionExpired = signal(false);

  private readonly API_URL = `${environment.apiBaseUrl}/api/v1`;
  private readonly tokenKey = 'bluedop_token'; // <-- Declared tokenKey
  private readonly mfaVerifiedKey = 'mfaVerified';
  private readonly mfaVerifiedLegacyKey = 'mfaVerfied';
  private readonly returnUrlKey = 'bluedop_returnUrl';
  currentUsers = signal<any>(null);

  constructor(
    private http: HttpClient, 
    private router: Router, 
    private toast: ToastService,
    private mfaService: MFAService
  ) {
    this.restoreSession();
    const user = this.getStoredItem('bluedop_user');
    if (user) {
      this.currentUsers.set(JSON.parse(user));
    }
  }

  /** =========================
   * Session Restore
   * ========================= */
  private restoreSession(): void {
    const user = this.getStoredItem('bluedop_user');
    const token = this.getStoredItem(this.tokenKey);

    if (user && token) {
      this._currentUser.set(this.mapUser(JSON.parse(user)));
      this._accessToken.set(token);
      this._isLoggedIn$.next(true);
    }
  }

  /** =========================
   * Authentication
   * ========================= */
  login(username: string, password: string): Observable<LoginResponse> {
    const body = new HttpParams()
      .set('grant_type', 'password')
      .set('username', username)
      .set('password', password);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<LoginResponse>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.LOGIN}`,
      body.toString(),
      { headers }
    ).pipe(
      map(res => normalizeLoginResponse(res)),
      tap(res => {
        this.handleLoginResponse(res);
      }),
      catchError(err => this.handleLoginError(err))
    );
  }

  loginWithPin(username: string, pin: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}${API_ENDPOINTS.AUTH.LOGIN_PIN}`, { username, pin })
      .pipe(
        map(res => normalizeLoginResponse(res)),
        tap(res => {
          this.handleLoginResponse(res);
        }),
        catchError(err => this.handleLoginError(err))
      );
  }

  private handleMFAChallenge(challenge: MFAChallenge): void {
    this.mfaService.handleMFAChallenge(challenge);
    if (challenge.session) {
      this.mfaService.setLoginMFASession(challenge.session, this.mapChallengeMethod(challenge.challengeName));
      localStorage.setItem('bluedop_mfa_login_challenge_name', challenge.challengeName);
    }
  }

  private mapChallengeMethod(challengeName: string): MFAMethod {
    const normalized = (challengeName || '').toUpperCase();
    if (normalized.includes('WHATSAPP')) return MFAMethod.WHATSAPP;
    if (normalized.includes('SMS')) return MFAMethod.SMS;
    if (normalized.includes('EMAIL') || normalized.includes('GMAIL')) return MFAMethod.EMAIL;
    return MFAMethod.TOTP;
  }

  private handleLoginResponse(response: LoginResponse): void {
    response = normalizeLoginResponse(response);

    const userRole = response.user?.role ?? (response as any)?.data?.user?.role;
    const loginData = (response as any)?.data ?? {};
    const loginUsername = response.user?.username ?? (response as any)?.username ?? loginData.username;

    const isSuperAdmin = String(userRole || '').trim().toLowerCase() === 'super admin'
      || String(loginUsername || '').trim().toLowerCase() === 'bluedopadmin';

    if (isSuperAdmin && response.access_token) {
      this.handleLoginSuccess(response, true);
      return;
    }

    if (response.mfaChallenge) {
      this.handleMFAChallenge(response.mfaChallenge);
      return;
    }

    if (response.access_token) {
      this.mfaService.setSetupAccessToken(response.access_token);
    }

    // Backend returns either:
    // - access_token (no MFA required / or MFA already satisfied)
    // - session_token (MFA required; verify page will complete login)
    const loginSessionToken = response.session_token;

    if (loginSessionToken) {
      // MFA flow: backend returns context under `response.data` (sample payload)
      const loginData = (response as any)?.data ?? {};
      const loginUsername = (response as any)?.username ?? loginData.username;
      const loginChallengeName = (response as any)?.challenge_name ?? (response as any)?.challengeName ?? loginData.challenge_name ?? loginData.challengeName;

      // Persist MFA context for the verify request
      if (loginUsername) {
        localStorage.setItem('bluedop_mfa_login_username', String(loginUsername));
      }
      if (loginChallengeName) {
        localStorage.setItem('bluedop_mfa_login_challenge_name', String(loginChallengeName));
      }

      this.clearAuthenticatedSession();
      this.storeMFAVerified(false);
      this.mfaService.setLoginMFASession(loginSessionToken);
      return;
    }


    if (response.access_token) {
      this.handleLoginSuccess(response, false);
    }
  }

  private handleLoginSuccess(response: LoginResponse, mfaVerified: boolean): void {
    response = normalizeLoginResponse(response);

    const user = response.user ? this.mapUser(response.user) : this._currentUser();

    this._currentUser.set(user);
    const accessToken = response.access_token || this._accessToken() || localStorage.getItem(this.tokenKey);
    this._accessToken.set(accessToken || null);

    if (user) {
      this.setStoredItem('bluedop_user', JSON.stringify(user));
      this.currentUsers.set(user);
    }
    if (accessToken) {
      this.setStoredItem(this.tokenKey, accessToken);
    }
    this.storeMFAVerified(mfaVerified);

    this._isLoggedIn$.next(true);
    if (mfaVerified) {
      this.toast.success(authText('loginSuccess'));
    }
  }

  completeMfaLogin(code: string): Observable<LoginResponse> {
    return this.mfaService.verifyLoginMFA(code).pipe(
      map((response: any) => normalizeLoginResponse(response)),
      tap((response: LoginResponse) => {
        this.handleLoginSuccess(response, true);
      })
    );
  }

  completeTOTPLogin(code: string, sessionToken?: string): Observable<LoginResponse> {
    return this.mfaService.verifyLoginTOTP(code, sessionToken).pipe(
      map((response: any) => normalizeLoginResponse(response)),
      tap((response: LoginResponse) => {
        this.handleLoginSuccess(response, true);
      })
    );
  }

  completeWhatsAppLogin(code: string, sessionToken?: string): Observable<LoginResponse> {
    return this.mfaService.verifyWhatsApp(code, sessionToken).pipe(
      map((response: any) => normalizeLoginResponse(response)),
      tap((response: LoginResponse) => {
        this.handleLoginSuccess(response, true);
      })
    );
  }

  completeSMSLogin(code: string, sessionToken?: string): Observable<LoginResponse> {
    return this.mfaService.verifySMS(code, sessionToken).pipe(
      map((response: any) => normalizeLoginResponse(response)),
      tap((response: LoginResponse) => {
        this.handleLoginSuccess(response, true);
      })
    );
  }

  completeGmailLogin(code: string, sessionToken?: string): Observable<LoginResponse> {
    return this.mfaService.verifyGmail(code, sessionToken).pipe(
      map((response: any) => normalizeLoginResponse(response)),
      tap((response: LoginResponse) => {
        this.handleLoginSuccess(response, true);
      })
    );
  }

  completeMFASetup(response: any): void {
    this.handleLoginSuccess(normalizeLoginResponse(response), true);
  }

  private clearAuthenticatedSession(): void {
    this._currentUser.set(null);
    this._accessToken.set(null);
    this.currentUsers.set(null);
    localStorage.removeItem('bluedop_user');
    localStorage.removeItem('bluedop_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  private mapUser(u: any): AuthUser {
    return {
      id: u.id ?? u.id,
      username: u.username ?? u.username,
      firstName: u.firstName ?? u.first_name ?? '',
      lastName: u.lastName ?? u.last_name ?? '',
      email: u.email ?? u.email,
      roleId: u.roleId ?? u.role_id ?? '',
      role: u.role ?? u.role_name ?? null,
      clinicId: u.clinicId ?? u.clinic_id ?? null,
      clinicName: u.clinicName ?? u.clinic_name ?? null,
      entityId: u.entityId ?? u.entity_id ?? null,
    };
  }

  get isSuperAdmin(): boolean {
    return this.hasRole(RoleName.SUPER_ADMIN);
  }

  get isEntityAdmin(): boolean {
    return this.hasRole(RoleName.ENTITY_ADMIN);
  }

  get isClinicAdmin(): boolean {
    return this.hasRole(RoleName.CLINIC_ADMIN);
  }

  get isStandardUser(): boolean {
    return this.hasRole(RoleName.STANDARD_USER);
  }

  getToken(): string | null {
    return this._accessToken();
  }

  hasToken(): boolean {
    return !!this.getStoredItem(this.tokenKey);
  }

  logout(redirect: boolean = true): void {
    const performCleanup = () => {
      this._currentUser.set(null);
      this._accessToken.set(null);
      this.sessionExpired.set(false);
      this.removeStoredItem('bluedop_user');
      this.removeStoredItem('bluedop_token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      this.storeMFAVerified(false);

      // Clear MFA state on logout (including MFA-login context)
      this.mfaService.clearAllMFAState();
      localStorage.removeItem('bluedop_mfa_login_username');
      localStorage.removeItem('bluedop_mfa_login_challenge_name');
      this._isLoggedIn$.next(false);
      if (redirect) {
        this.router.navigate(['/login']);
      }
    };

    if (this.hasToken()) {
      this.http.post(`${this.API_URL}${API_ENDPOINTS.AUTH.LOGOUT}`, {}).subscribe({
        next: () => performCleanup(),
        error: (err) => {
          console.error('Logout API failed:', err);
          performCleanup();
        }
      });
    } else {
      performCleanup();
    }
  }

  /** Clears session and shows the session-expired modal instead of hard-redirecting. */
  triggerSessionExpiry(): void {
    this._currentUser.set(null);
    this._accessToken.set(null);
    this.removeStoredItem('bluedop_user');
    this.removeStoredItem('bluedop_token');
    localStorage.removeItem('token~');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    this.storeMFAVerified(false);
    this._isLoggedIn$.next(false);
    this.sessionExpired.set(true);
  }

  /** =========================
   * Password Recovery
   * ========================= */
  forgotPassword(username: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.FORGOT_PASSWORD}`,
      { username }
    );
  }

  resetPassword(token: string, newPassword: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.RESET_PASSWORD}`,
      { token, new_password: newPassword }
    );
  }

  verifyEmail(token: string, username: string, password: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.VERIFY_EMAIL(token)}`,
      { username, password }
    );
  }

  /** =========================
   * Error Handling
   * ========================= */
  private handleLoginError(error: HttpErrorResponse) {
    // Check for MFA challenge (usually 403 with mfaRequired or specific challenge)
    if (error.error?.mfaChallenge) {
      this.handleMFAChallenge(error.error.mfaChallenge);
      return throwError(() => new Error('MFA_REQUIRED'));
    }

    let msg = '';

    if (error.error) {
      if (typeof error.error === 'string') {
        msg = error.error;
      } else if (typeof error.error === 'object') {
        msg = error.error.message || error.error.detail || error.error.error || error.error.data?.detail;
      }
    }

    if (!msg || typeof msg !== 'string') {
      msg = authText('requestFailed');
      if (error.status === 401) msg = authText('invalidCredentials');
      if (error.status === 423) msg = authText('accountLocked');
      if (error.status === 403) msg = authText('inactiveDenied');
    }

    this.toast.error(msg);
    return throwError(() => new Error(msg));
  }

  private handleError(error: HttpErrorResponse) {
    let msg = '';

    if (error.error) {
      if (typeof error.error === 'string') {
        msg = error.error;
      } else if (typeof error.error === 'object') {
        msg = error.error.message || error.error.detail || error.error.error || error.error.data?.detail;
      }
    }

    if (!msg || typeof msg !== 'string') {
      msg = authText('requestFailed');
      if (error.status === 401) msg = authText('invalidCredentials');
      if (error.status === 423) msg = authText('accountLocked');
      if (error.status === 403) msg = authText('inactiveDenied');
    }

    this.toast.error(msg);
    return throwError(() => new Error(msg));
  }
  /** =========================
   * RBAC
   * ========================= */
  get role(): RoleName | null {
    return this._currentUser()?.role ?? null;
  }

  private normalizedRole(): string {
    return (this.role ?? '').toString().trim().toLowerCase();
  }

  hasRole(role: RoleName): boolean {
    return this.normalizedRole() === role.toString().trim().toLowerCase();
  }

  hasAnyRole(roles: RoleName[]): boolean {
    const currentRole = this.normalizedRole();
    return !!currentRole && roles.some(role => currentRole === role.toString().trim().toLowerCase());
  }

  hasPermission(permission: Permission): boolean {
    const role = this.role;
    if (!role) return false;
    const normalized = this.normalizedRole();
    const matchedRole = (Object.values(RoleName) as RoleName[]).find(
      (candidate) => candidate.toString().trim().toLowerCase() === normalized
    );
    return matchedRole ? (ROLE_PERMISSIONS[matchedRole]?.includes(permission) ?? false) : false;
  }

  get isLoggedIn(): boolean {
    return !!this._accessToken() && !this.isTokenExpired();
  }

  normalizeReturnUrl(url: string | null | undefined): string | null {
    try {
      const candidate = url?.trim();
      if (!candidate || !candidate.startsWith('/') || candidate.startsWith('//') || candidate.includes('\\')) {
        return null;
      }
      if (/[\u0000-\u001F\u007F]/.test(candidate)) {
        return null;
      }

      const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
      const parsed = new URL(candidate, baseOrigin);
      if (parsed.origin !== baseOrigin) {
        return null;
      }

      const decodedPathname = decodeURIComponent(parsed.pathname);
      if (decodedPathname.startsWith('//') || decodedPathname.includes('\\')) {
        return null;
      }

      if (this.isAuthPagePath(parsed.pathname) || this.isAuthPagePath(decodedPathname)) {
        return null;
      }

      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return null;
    }
  }

  setReturnUrl(url: string): void {
    try {
      const returnUrl = this.normalizeReturnUrl(url);
      if (!returnUrl) return;
      localStorage.setItem(this.returnUrlKey, returnUrl);
    } catch {
    }
  }

  getAndClearReturnUrl(): string | null {
    try {
      const url = localStorage.getItem(this.returnUrlKey);
      if (url) localStorage.removeItem(this.returnUrlKey);
      return this.normalizeReturnUrl(url);
    } catch {
      return null;
    }
  }

  get isMFAVerified(): boolean {
    const role = this._currentUser()?.role;
    const username = this._currentUser()?.username;
    if (String(role || '').trim().toLowerCase() === 'super admin' || String(username || '').trim().toLowerCase() === 'bluedopadmin') {
      return true;
    }
    return sessionStorage.getItem(this.mfaVerifiedKey) === 'true'
      || sessionStorage.getItem(this.mfaVerifiedLegacyKey) === 'true';
  }

  private storeMFAVerified(value: boolean): void {
    const serialized = String(value);
    sessionStorage.setItem(this.mfaVerifiedKey, serialized);
    sessionStorage.setItem(this.mfaVerifiedLegacyKey, serialized);
  }

  private isAuthPagePath(pathname: string): boolean {
    return pathname === '/login'
      || pathname.startsWith('/login/')
      || pathname === '/forgot-password'
      || pathname.startsWith('/forgot-password/')
      || pathname === '/reset-password'
      || pathname.startsWith('/reset-password/')
      || pathname === '/verify-email'
      || pathname.startsWith('/verify-email/');
  }

  isTokenExpired(): boolean {

    const token = this._accessToken();
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // exp is in seconds
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }

  private getStoredItem(key: string): string | null {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  }

  private setStoredItem(key: string, value: string): void {
    localStorage.setItem(key, value);
    sessionStorage.removeItem(key);
  }

  private removeStoredItem(key: string): void {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }
}
